import { useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Scale, Loader2, CircleCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";
import { PasswordStrength } from "@/components/auth/PasswordStrength";

const MIN_LEN = 12;

export function ResetPasswordPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < MIN_LEN) {
      setError(`Password must be at least ${MIN_LEN} characters.`);
      return;
    }
    if (pw !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "This reset link is invalid or has expired.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,hsl(var(--primary)/0.06),transparent_60%)]" />
      <div className="relative mx-auto grid min-h-screen max-w-md place-items-center px-6">
        <div className="w-full">
          <div className="mb-8 flex flex-col items-center text-center">
            <span className="mb-4 grid h-12 w-12 place-items-center rounded-lg bg-primary text-primary-foreground shadow-card">
              <Scale className="h-6 w-6" />
            </span>
            <h1 className="font-serif text-3xl font-semibold tracking-tight">Set a new password</h1>
            <p className="mt-2 text-sm text-muted-foreground">Choose a strong password of at least {MIN_LEN} characters.</p>
          </div>

          <div className="rounded-lg border border-border bg-surface p-7 shadow-card">
            {done ? (
              <div className="space-y-4 text-center" data-testid="reset-password-success">
                <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-status-paid/10 text-status-paid">
                  <CircleCheck className="h-6 w-6" />
                </span>
                <p className="text-sm text-foreground">Your password has been reset. Please sign in with your new password.</p>
                <Button className="w-full" onClick={() => navigate("/login")} data-testid="reset-go-to-login-button">
                  Go to sign in
                </Button>
              </div>
            ) : !token ? (
              <div className="space-y-4 text-center" data-testid="reset-password-missing-token">
                <p className="text-sm text-destructive">This reset link is invalid or has expired.</p>
                <Button variant="secondary" className="w-full" onClick={() => navigate("/forgot-password")}>
                  Request a new link
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="rp-pw">New password</Label>
                  <Input
                    id="rp-pw"
                    type="password"
                    autoComplete="new-password"
                    data-testid="reset-password-input"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 12 characters"
                    required
                  />
                  <PasswordStrength value={pw} />
                </div>
                <div>
                  <Label htmlFor="rp-confirm">Confirm new password</Label>
                  <Input
                    id="rp-confirm"
                    type="password"
                    autoComplete="new-password"
                    data-testid="reset-password-confirm-input"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your new password"
                    required
                  />
                </div>

                {error && (
                  <p data-testid="reset-password-error" className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error}
                  </p>
                )}

                <Button type="submit" size="lg" className="w-full" disabled={submitting} data-testid="reset-password-submit">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  Reset password
                </Button>
                <div className="text-center">
                  <button type="button" onClick={() => navigate("/login")} className="inline-flex items-center gap-1 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
